import { useState } from "react";
import { Input } from "../../ui"; // Componente Input reutilizable
import {
  AlertaProps,
  EditarPerfilProps,
  FormEditarPerfil,
  FormErrors,
} from "@/types"; // Tipos
import { isValidPostalCode, isValidEmail } from "../../validation/validations"; // Funciones de validación

// Componente de formulario para editar perfil
const Formulario: React.FC<EditarPerfilProps> = ({
  formEditarPerfil,
  setFormEditarPerfil,
  usuario,
}) => {
  // Estado local para manejar errores de validación
  const [errors, setErrors] = useState<FormErrors>({});

  // Maneja cambios generales en los inputs
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const nameParts = name.split("."); // Permite manejar inputs anidados (ej: direccion.calle)

    if (nameParts.length === 1) {
      // Si el input no es anidado, actualiza directamente
      setFormEditarPerfil({ ...formEditarPerfil, [name]: value });
    } else {
      // Si el input es anidado, actualiza la propiedad correspondiente dentro del objeto
      const key = nameParts[0] as keyof FormEditarPerfil;
      setFormEditarPerfil({
        ...formEditarPerfil,
        [key]: { ...(formEditarPerfil[key] as any), [nameParts[1]]: value },
      });
    }
  };

  // Maneja cambios en el correo electrónico con validación
  const handleCorreoElectronicoChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;
    const isValid = isValidEmail(value); // Valida que sea un correo válido

    setFormEditarPerfil((prevData) => ({
      ...prevData,
      correo_perfil: value, // Actualiza el estado del correo
    }));

    // Establece error si no es válido, lo borra si es válido
    setErrors((prevErrors) => ({
      ...prevErrors,
      correo_perfil: isValid
        ? false
        : ({
            type: "correo_perfil",
            mensaje: "El correo electrónico no es válido.",
          } as unknown as AlertaProps),
    }));
  };

  // Maneja cambios en el código postal con validación
  const handleCodigoPostalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const isValid = isValidPostalCode(value); // Valida que sea un código postal válido

    setFormEditarPerfil((prevData) => ({
      ...prevData,
      direccion: {
        ...prevData.direccion!,
        codigo_postal: parseInt(e.target.value, 10) || 0, // Convierte a number
      },
    }));

    // Actualiza errores según la validez del código postal
    setErrors((prevErrors) => ({
      ...prevErrors,
      postalCode: isValid
        ? false
        : ({
            type: "codigoPostal",
            mensaje: "El código postal debe ser de 5 dígitos.",
          } as unknown as AlertaProps),
    }));
  };

  const [codigoCopiado, setCodigoCopiado] = useState(false);

  const copiarCodigoAfiliado = async () => {
    const codigo = formEditarPerfil.codigo_afiliado;

    if (!codigo) return;

    try {
      await navigator.clipboard.writeText(codigo);

      setCodigoCopiado(true);

      setTimeout(() => {
        setCodigoCopiado(false);
      }, 2000);
    } catch (error) {
      console.error("Error al copiar código:", error);
    }
  };

  return (
    <div className="grid xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 py-6">
      {/* Razón social o nombre completo, solo lectura */}
      <div className="col-span-1">
        <Input
          id="razonSocial"
          name="razonSocial"
          value={
            formEditarPerfil.nombre_empresa ||
            (usuario ? `${usuario.nombre} ${usuario.apellidos}` : "")
          }

          readOnly
          disabled
          className="dark:bg-blue-100 cursor-not-allowed"
        />
      </div>

      {/* Nombre comercial */}
      <div className="col-span-1">
        <Input
          id="nombreComercial"
          type="text"
          name="nombre_comercial"
          value={formEditarPerfil.nombre_comercial || ""}
          onChange={handleChange}
          placeholder="Nombre comercial"
          className={errors.nombre_comercial ? "bg-red-100" : ""}
        />
      </div>

      {/* Calle */}
      <div className="col-span-1">
        <Input
          id="calle"
          type="text"
          name="direccion.calle"
          value={formEditarPerfil.direccion?.calle || ""}
          onChange={handleChange}
          placeholder="Calle/nº/bloque/piso..."
          className={errors.calle ? "bg-red-100" : ""}
        />
      </div>

      {/* Localidad */}
      <div className="col-span-1">
        <Input
          id="localidad"
          type="text"
          name="direccion.localidad"
          value={formEditarPerfil.direccion?.localidad || ""}
          onChange={handleChange}
          placeholder="Localidad"
          className={errors.localidad ? "bg-red-100" : ""}
        />
      </div>

      {/* Provincia */}
      <div className="col-span-1">
        <Input
          id="provincia"
          type="text"
          name="direccion.provincia"
          value={formEditarPerfil.direccion?.provincia || ""}
          onChange={handleChange}
          placeholder="Provincia"
          className={errors.provincia ? "bg-red-100" : ""}
        />
      </div>

      {/* País, solo lectura */}
      <div className="col-span-1">
        <Input
          type="text"
          value={formEditarPerfil.direccion?.pais || ""}
          readOnly
          disabled
          className="dark:bg-blue-100 cursor-not-allowed"
        />
      </div>

      {/* Código postal */}
      <div className="col-span-1">
        <Input
          id="codigoPostal"
          type="text"
          name="direccion.codigo_postal"
          value={formEditarPerfil.direccion?.codigo_postal || ""}
          onChange={handleCodigoPostalChange}
          placeholder="Código Postal"
          className={errors.postalCode ? "bg-red-100" : ""}
        />
      </div>

      {/* Correo electrónico */}
      <div className="col-span-1">
        <Input
          id="email"
          type="email"
          name="correo_perfil"
          value={formEditarPerfil.correo_perfil || ""}
          onChange={handleCorreoElectronicoChange}
          placeholder="Correo electrónico"
          className={errors.correo_perfil ? "bg-red-100" : ""}
        />
      </div>

      {/* Página web */}
      <div className="col-span-1">
        <Input
          id="web"
          type="text"
          name="url_web"
          value={formEditarPerfil.url_web || ""}
          onChange={handleChange}
          placeholder="Página web"
          className={errors.url_web ? "bg-red-100" : ""}
        />
      </div>

      {/* DNI/NIE */}
      {formEditarPerfil.dniNie && (
        <div className="col-span-1">
          <Input
            id="dniNie"
            type="text"
            name="dniNie"
            placeholder="DNI / NIE"
            value={formEditarPerfil.dniNie || ""}
            disabled
            className="dark:bg-blue-100 cursor-not-allowed"
          />
        </div>
      )}

      {/* CIF */}
      {formEditarPerfil.cif && (
        <div className="col-span-1">
          <Input
            id="cif"
            type="text"
            name="cif"
            placeholder="CIF"
            value={formEditarPerfil.cif || ""}
            disabled
            className="dark:bg-blue-100 cursor-not-allowed"
          />
        </div>
      )}

      {/* ================= INFORMACIÓN DE AFILIADO ================= */}

      <div className="col-span-full mt-2">
        <div
          className="
      rounded-2xl
      border border-b-2 border-r-2 border-zinc-200
      bg-zinc-600
      shadow-sm shadow-blue-300
      p-5
      dark:border-gray-700
      dark:bg-gray-800/40
     
    "
        >
          {/* Cabecera */}
          <div className="mb-5">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">
              Información de afiliado
            </h3>

            <p className="mt-1 text-sm text-gray-900 dark:text-white">
              Consulta tu código de afiliado y los puntos conseguidos.
            </p>
          </div>

          {/* Tarjetas */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Código de afiliado */}
            <div
              className="
               rounded-2xl
               border border-gray-200
               bg-gradient-to-br from-white to-gray-50
               p-5
               shadow-sm
               transition-all
               hover:shadow-md
               dark:border-gray-700
               dark:from-gray-800
               dark:to-gray-900
             "
            >
              <div
                onClick={copiarCodigoAfiliado}
                className="
                  flex cursor-pointer items-center gap-4
                  transition-opacity hover:opacity-80
                "
                title="Pulsa para copiar el código"
              >
                <div
                  className={`
                   flex h-12 w-12
                   shrink-0
                   cursor-pointer
                   items-center justify-center
                   rounded-xl
                   text-xl
                   border-2
                   transition-all
                   duration-300
                   hover:scale-105
                   hover:shadow-md
                   dark:bg-blue-900/40
               
                   ${
                     codigoCopiado
                       ? `
                         border-green-500
                         bg-green-100
                         text-green-600
                         shadow-md
                         shadow-green-500/40
                         scale-105
                       `
                       : `
                         border-transparent
                         bg-blue-100
                         hover:shadow-blue-500/30
                       `
                   }
                 `}
                >
                  {codigoCopiado ? "✓" : "🎟️"}
                </div>

                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Código descuento
                  </p>

                  <p className="mt-1 text-xl font-bold tracking-wider text-gray-900 dark:text-white">
                    {formEditarPerfil.codigo_afiliado || "Sin código"}
                  </p>

                  <p
                    className={`mt-1 text-xs ${
                      codigoCopiado
                        ? "text-green-500 font-medium"
                        : "text-gray-300 dark:text-gray-400"
                    }`}
                  >
                    {codigoCopiado ? "✓ Código copiado" : "Pulsa para copiar"}
                  </p>
                </div>
              </div>
            </div>

            {/* Puntos conseguidos */}
            <div
              className="
          rounded-2xl
          border border-gray-200
          bg-gradient-to-br from-white to-gray-50
          p-5
          shadow-sm
          transition-all
          hover:shadow-md
          dark:border-gray-700
          dark:from-gray-800
          dark:to-gray-900
        "
            >
              <div className="flex items-center gap-4">
                <div
                  className="
              flex h-12 w-12
              shrink-0
              items-center justify-center
              rounded-xl
              bg-amber-100
              text-xl
              dark:bg-amber-900/40
            "
                >
                  ⭐
                </div>

                <div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Puntos conseguidos
                  </p>

                  <p className="mt-1 text-2xl font-bold text-gray-900 dark:text-white">
                    {formEditarPerfil.puntos ?? 0}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Formulario;
